import {
    ADDITIONAL_ROLES,
    SUBSCRIPTION_PLANS,
    canAccessDomainSettings,
    canEditSomePage,
    canSubmitFeedback,
    hasFeatureFlag,
    hasPortfolioSummaryDashboard,
    hasProductLine,
    hasResourceCenterAccess,
    hasSegmentFlag,
    hasSessionReplayViewerAccess,
    hasUserPermissions,
    isAdoptOnCore,
    isDigitalAdoptionOnly,
    isPartnerAnalyticsSub,
    isRouteDisabledForSubscriptionPlan,
    isScimEnabled,
} from './route-guards.utils';

import { USER_ROLES } from '@/constants';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import { EXPLORE_STATUSES, FREEMIUM_STATUSES, getExploreStatus, getFreemiumStatus } from '@/voc/helpers/permissions.helper';
const DEFAULT_PERMISSIONS_REDIRECT = {
    name: 'NoAccess',
};

export function generateUpgradeRequiredRoute (featureKey) {
    return {
        name: 'UpgradeRequired',
        params: {
            featureKey,
        },
    };
}

export function hasResourceCenterRedirect () {
    if (!hasProductLine('guides')) return DEFAULT_PERMISSIONS_REDIRECT;
    const maxPublicResourceCentersAllowed = hasProductLine('guides', 'maxPublicResourceCentersAllowed');
    const isAeu = hasProductLine('adoptEndUser');
    const disableRCForAeu = !maxPublicResourceCentersAllowed && isAeu;

    if (isRouteDisabledForSubscriptionPlan([SUBSCRIPTION_PLANS.PENDO_FREE, SUBSCRIPTION_PLANS.PENDO_STARTER])) {
        return generateUpgradeRequiredRoute('resourceCenter');
    }

    if (!hasResourceCenterAccess() || disableRCForAeu) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasThemesRedirect () {
    if (!hasProductLine('guides')) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasUserPermissions(USER_ROLES.ADMIN)) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasSessionReplayRedirect () {
    if (isAdoptOnCore() || isDigitalAdoptionOnly()) return DEFAULT_PERMISSIONS_REDIRECT;

    if (!hasProductLine('sessionReplay')) {
        return { name: 'BuySessionReplay' };
    }

    if (!hasSessionReplayViewerAccess()) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasJourneysRedirect () {
    if (!hasProductLine('orchestrate')) return { name: 'BuyOrchestrate' };

    return false;
}

export function hasStandaloneEmailsRedirect () {
    if (!hasProductLine('orchestrate', 'emailStandAloneEnabled')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasBasicGuidesRedirect () {
    if (!hasProductLine('guides')) return { name: 'GuidesBasicUpsell' };

    return false;
}

export function hasGuidesOrderingRedirect () {
    if (hasProductLine(PRODUCT_LINES.ADOPT)) {
        return DEFAULT_PERMISSIONS_REDIRECT;
    }

    return hasBasicGuidesRedirect();
}

export function hasPredictUpsellRedirect () {
    if (hasProductLine('predict')) return { name: 'Home' };

    return false;
}

export function hasSalesforceUpsellRedirect () {
    if (!hasSegmentFlag('salesforceUpsell')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasGuideAuditRedirect () {
    if (!hasProductLine('guides')) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasSegmentFlag('guideAudit')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasGuideExperimentsRedirect () {
    if (!hasProductLine('guides', 'experimentsEnabled')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasAbTestingRedirect () {
    if (!hasProductLine('guides', 'experimentsEnabled')) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasSegmentFlag('abTestingBeta')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasFormValidationRedirect () {
    if (!hasProductLine('guides', 'formValidationEnabled') || hasProductLine('adoptEndUser')) {
        return DEFAULT_PERMISSIONS_REDIRECT;
    }

    return false;
}

export function hasListenSettingsRedirect () {
    const hasFreemiumEnabled = hasProductLine('voc', 'freemium.enabled');
    if (!hasProductLine('voc') || hasFreemiumEnabled) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasListenStatusesRedirect () {
    if (!hasProductLine('voc') || hasProductLine('adoptEndUser')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasListenSettingsTemplatesRedirect () {
    if (!hasProductLine('voc') || hasProductLine('adoptEndUser')) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasSegmentFlag('listenEmailsV3') && !window.Cypress) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasListenSettingsJiraConnectRedirect () {
    if (!hasProductLine('voc') || hasProductLine('adoptEndUser')) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasSegmentFlag('listenJiraConnect') && !window.Cypress) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasUserPermissions(USER_ROLES.ADMIN)) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasFeedbackSourcesRedirect () {
    const hasFreemiumEnabled = hasProductLine('voc', 'freemium.enabled');
    if (!hasProductLine('voc', 'feedback') || hasFreemiumEnabled) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!canSubmitFeedback()) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasListenFeedbackRedirect () {
    const hasFreemiumEnabled = hasProductLine('voc', 'freemium.enabled');
    if (!hasProductLine('voc', 'feedback') || hasFreemiumEnabled) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasListenAllFeedbackRedirect () {
    const hasFreemiumEnabled = hasProductLine('voc', 'freemium.enabled');
    if (!hasProductLine('voc', 'feedback') || hasFreemiumEnabled) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasListenFreemiumRedirect () {
    const freemiumStatus = getFreemiumStatus();
    if (freemiumStatus === FREEMIUM_STATUSES.OFF || freemiumStatus === FREEMIUM_STATUSES.NOT_ALLOWED) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasListenExploreRedirect () {
    const hasFreemiumEnabled = hasProductLine('voc', 'freemium.enabled');
    const exploreStatus = getExploreStatus();
    if (exploreStatus === EXPLORE_STATUSES.OFF || exploreStatus === EXPLORE_STATUSES.NOT_ALLOWED || hasFreemiumEnabled) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasValidateRedirect () {
    const hasFreemiumEnabled = hasProductLine('voc', 'freemium.enabled');
    if (!hasProductLine('voc', 'ideas') || hasFreemiumEnabled) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasBasicAnalyticsRedirect () {
    if (!hasProductLine('analytics')) return { name: 'AnalyticsUpsell' };

    return false;
}

export function hasInsightsRedirect () {
    if (!hasProductLine('analytics', 'insightsEnabled')) {
        return DEFAULT_PERMISSIONS_REDIRECT;
    }
    if (!hasSegmentFlag('insights')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasDataExplorerAccessRedirect () {
    if (!hasProductLine('analytics', 'dataExplorerEnabled')) {
        return DEFAULT_PERMISSIONS_REDIRECT;
    }

    if (
        isRouteDisabledForSubscriptionPlan([
            SUBSCRIPTION_PLANS.PENDO_FREE,
            SUBSCRIPTION_PLANS.PENDO_STARTER,
            SUBSCRIPTION_PLANS.PENDO_STARTER_2024,
        ])
    ) {
        return generateUpgradeRequiredRoute('dataExplorer');
    }

    return false;
}

export function hasRetentionAccessRedirect () {
    if (isAdoptOnCore() && !hasProductLine('analytics', 'retentionEnabled')) {
        return DEFAULT_PERMISSIONS_REDIRECT;
    }

    if (!hasProductLine('analytics')) return { name: 'AnalyticsUpsell' };

    return false;
}

export function hasFunnelsAccessRedirect () {
    if (!hasProductLine('analytics', 'funnelsEnabled')) {
        if (isAdoptOnCore()) {
            return DEFAULT_PERMISSIONS_REDIRECT;
        }

        return { name: 'AnalyticsUpsell' };
    }

    return false;
}

export function hasPathsAccessRedirect () {
    if (!hasProductLine('analytics', 'pathsEnabled')) {
        if (isAdoptOnCore()) {
            return DEFAULT_PERMISSIONS_REDIRECT;
        }

        return { name: 'AnalyticsUpsell' };
    }

    return false;
}

export function hasWorkflowsRedirect () {
    if (!hasProductLine('analytics', 'workflowEnabled')) {
        if (isAdoptOnCore()) {
            return DEFAULT_PERMISSIONS_REDIRECT;
        }

        return { name: 'AnalyticsUpsell' };
    }

    if (
        isRouteDisabledForSubscriptionPlan([
            SUBSCRIPTION_PLANS.PENDO_FREE,
            SUBSCRIPTION_PLANS.PENDO_STARTER,
            SUBSCRIPTION_PLANS.PENDO_STARTER_2024,
        ])
    ) {
        return generateUpgradeRequiredRoute('workflowList');
    }

    return false;
}

export function hasPESRedirect () {
    if (!hasProductLine('analytics', 'pesEnabled')) {
        return DEFAULT_PERMISSIONS_REDIRECT;
    }

    if (
        isRouteDisabledForSubscriptionPlan([
            SUBSCRIPTION_PLANS.PENDO_FREE,
            SUBSCRIPTION_PLANS.PENDO_STARTER,
            SUBSCRIPTION_PLANS.PENDO_STARTER_2024,
        ])
    ) {
        return generateUpgradeRequiredRoute('productEngagementScore');
    }

    return false;
}

export function hasWebAnalyticsRedirect () {
    if (!hasProductLine('analytics', 'webAnalyticsEnabled')) {
        return DEFAULT_PERMISSIONS_REDIRECT;
    }

    if (
        isRouteDisabledForSubscriptionPlan([
            SUBSCRIPTION_PLANS.PENDO_FREE,
            SUBSCRIPTION_PLANS.PENDO_STARTER,
            SUBSCRIPTION_PLANS.PENDO_STARTER_2024,
        ])
    ) {
        return generateUpgradeRequiredRoute('webAnalytics');
    }

    return false;
}

export function hasProductAreasRedirect () {
    if (isRouteDisabledForSubscriptionPlan([SUBSCRIPTION_PLANS.PENDO_FREE])) {
        return generateUpgradeRequiredRoute('productAreas');
    }

    return false;
}

export function hasSubEssentialContactsRedirect () {
    if (!hasUserPermissions(USER_ROLES.ADMIN)) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasSegmentFlag('essentialContacts')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasExtensionSettingsRedirect () {
    if (!hasUserPermissions(USER_ROLES.ADMIN)) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasProductLine('oneUIEngineeringUse', 'extensionApplicationEnabled') || hasProductLine('adoptEndUser')) {
        return DEFAULT_PERMISSIONS_REDIRECT;
    }

    return false;
}

export function hasAdminLicenseUtilizationRedirect () {
    if (!hasUserPermissions(USER_ROLES.ADMIN)) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasProductLine('oneUIEngineeringUse', 'licenseUtilizationEnabled') || hasProductLine('adoptEndUser')) {
        return DEFAULT_PERMISSIONS_REDIRECT;
    }

    return false;
}

export function hasDomainsRedirect () {
    if (!canAccessDomainSettings()) return DEFAULT_PERMISSIONS_REDIRECT;
    if (isAdoptOnCore()) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasUserPermissions(USER_ROLES.ADMIN)) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasErrorLogAndEventPropertiesRedirect () {
    if (!hasUserPermissions(USER_ROLES.ADMIN)) return DEFAULT_PERMISSIONS_REDIRECT;
    if (isAdoptOnCore()) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasIntegrationsRedirect () {
    if (
        isRouteDisabledForSubscriptionPlan([
            SUBSCRIPTION_PLANS.PENDO_FREE,
            SUBSCRIPTION_PLANS.PENDO_STARTER,
            SUBSCRIPTION_PLANS.PENDO_FREE_TRIAL,
        ])
    ) {
        return generateUpgradeRequiredRoute('integrations');
    }

    if (!hasUserPermissions(USER_ROLES.ADMIN)) return DEFAULT_PERMISSIONS_REDIRECT;
    if (isAdoptOnCore() && !hasFeatureFlag('allowIntegrationKey')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasIntegrationsCatalogRedirect () {
    if (
        isRouteDisabledForSubscriptionPlan([
            SUBSCRIPTION_PLANS.PENDO_FREE,
            SUBSCRIPTION_PLANS.PENDO_STARTER,
            SUBSCRIPTION_PLANS.PENDO_FREE_TRIAL,
        ])
    ) {
        return generateUpgradeRequiredRoute('integrations');
    }

    if (!hasUserPermissions(USER_ROLES.ADMIN)) return DEFAULT_PERMISSIONS_REDIRECT;
    if (isAdoptOnCore() && !hasFeatureFlag('allowIntegrationKey')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasOEMSettingsRedirect () {
    if (!hasUserPermissions(ADDITIONAL_ROLES.ADOPT_MANAGER)) return DEFAULT_PERMISSIONS_REDIRECT;
    if (isPartnerAnalyticsSub()) return false;
    if (!hasProductLine('adoptReseller')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasOEMSettingsApplicationRedirect () {
    if (!hasUserPermissions(USER_ROLES.ADMIN)) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasProductLine('adoptEndUser')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasAdminTeamsRedirect () {
    if (!hasUserPermissions(USER_ROLES.ADMIN)) return DEFAULT_PERMISSIONS_REDIRECT;

    const isElligibleForTeams = hasFeatureFlag('enableTeams') || isScimEnabled();

    if (!isElligibleForTeams) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasDataManagementRedirect () {
    if (
        isRouteDisabledForSubscriptionPlan([
            SUBSCRIPTION_PLANS.PENDO_FREE,
            SUBSCRIPTION_PLANS.PENDO_STARTER,
            SUBSCRIPTION_PLANS.PENDO_STARTER_2024,
        ])
    ) {
        return generateUpgradeRequiredRoute('dataManagement');
    }

    if (!hasUserPermissions(USER_ROLES.ADMIN)) return DEFAULT_PERMISSIONS_REDIRECT;
    if (isAdoptOnCore()) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasBasicOrgAdminRedirect ({ isAEU = false } = {}) {
    if (isAEU) {
        return DEFAULT_PERMISSIONS_REDIRECT;
    }

    const canAccessOrgPage =
        hasUserPermissions(ADDITIONAL_ROLES.ORG_ADMIN) ||
        hasUserPermissions(USER_ROLES.SUPER) ||
        hasUserPermissions(ADDITIONAL_ROLES.OPS);

    if (!canAccessOrgPage) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasRoadmapsRedirect () {
    if (!hasProductLine('roadmaps')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasPortfolioOverviewRedirect () {
    if (!hasProductLine('oneUIEngineeringUse', 'portfolioViewEnabled')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasPortfolioSummaryRedirect () {
    if (
        !hasSegmentFlag('portfolioInsights') &&
        (hasPortfolioSummaryDashboard() ||
            hasFeatureFlag('enableExecutiveDashboard') ||
            hasSegmentFlag('portfolioSummary'))
    ) {
        return false;
    }

    return DEFAULT_PERMISSIONS_REDIRECT;
}

export function hasPortfolioInsightsRedirect () {
    if (
        hasSegmentFlag('portfolioInsights') &&
        (hasUserPermissions(USER_ROLES.ADMIN) || hasSegmentFlag('nonAdminsCommandCenter'))
    ) {
        return false;
    }

    return DEFAULT_PERMISSIONS_REDIRECT;
}

export function hasApplicationUsageRedirect () {
    if (!hasProductLine('oneUIEngineeringUse', 'appUsageEnabled') || hasProductLine('adoptEndUser')) {
        return DEFAULT_PERMISSIONS_REDIRECT;
    }

    return false;
}

export function hasLicenseUtilizationRedirect () {
    if (!hasProductLine('oneUIEngineeringUse', 'licenseUtilizationEnabled')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasSurveysRedirect () {
    const hasSurveys = hasProductLine('surveys');
    const hasNPS = hasProductLine('nps');
    if (!hasSurveys && !hasNPS) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasSurveys && hasNPS) return { name: 'NPS' };

    return false;
}

export function hasAgentModeRedirect () {
    if (!hasFeatureFlag('askPendo') || !hasSegmentFlag('askPendo') || hasProductLine(PRODUCT_LINES.ADOPT)) {
        return DEFAULT_PERMISSIONS_REDIRECT;
    }

    return false;
}

export function hasCustomerFinderRedirect () {
    if (!hasSegmentFlag('customerFinder')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasListenAgentRedirect () {
    if (!hasSegmentFlag('listenAgent')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasSuperSubsListRedirect () {
    if (!hasUserPermissions(ADDITIONAL_ROLES.PENDOZER)) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasJobsAndAggsRedirect () {
    if (!hasUserPermissions(ADDITIONAL_ROLES.OPS)) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasSuperToolsRedirect () {
    if (!hasUserPermissions(USER_ROLES.SUPER)) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasSegmentFlag('superTools')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasSuperToolsMigrationRedirect () {
    if (!hasUserPermissions(USER_ROLES.SUPER)) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasSegmentFlag('superTools')) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasSegmentFlag('superToolsMigration')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasSuperAccessRedirect () {
    if (!hasUserPermissions(USER_ROLES.SUPER)) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasAiAgentsRedirect () {
    const hasAgentAnalyticsPL = hasProductLine('agentAnalytics');

    if (!hasAgentAnalyticsPL) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasNPSRedirect () {
    if (hasProductLine('surveys')) return { name: 'SurveysList' };
    if (!hasProductLine('nps')) return DEFAULT_PERMISSIONS_REDIRECT;
    if (hasProductLine('adoptEndUser')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasAccountsRedirect () {
    if (!hasFeatureFlag('enableAccounts') || !hasProductLine(PRODUCT_LINES.CORE, 'accountsUIEnabled')) {
        return DEFAULT_PERMISSIONS_REDIRECT;
    }

    return false;
}

export function hasParentAccountsRedirect () {
    if (hasAccountsRedirect() || !hasFeatureFlag('usesSubaccounts')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasHomeRedirect () {
    if (hasProductLine(PRODUCT_LINES.ADOPT) || !hasProductLine(PRODUCT_LINES.CORE, 'homepageEnabled')) {
        return { name: 'DefaultDashboard' };
    }

    return false;
}

export function hasTagMaintenanceRedirect () {
    if (!hasSegmentFlag('tagMaintenancePh1')) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!canEditSomePage()) return DEFAULT_PERMISSIONS_REDIRECT;
    if (hasProductLine('adoptEndUser')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasFeatureAlertsRedirect () {
    if (hasProductLine('adoptEndUser')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasPageAlertsRedirect () {
    if (hasProductLine('adoptEndUser')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasVisitorReportsRedirect () {
    if (!hasProductLine(PRODUCT_LINES.CORE, 'visitorReportsEnabled')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasOEMProductAnalyticsRedirect () {
    if (!hasProductLine('analytics') && hasProductLine('adoptEndUser')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasAppHealthRedirect () {
    if (!hasSegmentFlag('appsOverview')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasAppDetailsRedirect () {
    if (!hasSegmentFlag('appsOverview')) return DEFAULT_PERMISSIONS_REDIRECT;
    if (!hasSegmentFlag('appPerformanceDetails')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}

export function hasSpacesRedirect () {
    if (!hasSegmentFlag('spacesFlag')) return DEFAULT_PERMISSIONS_REDIRECT;

    return false;
}
